/**
 * QR Code Details Page
 * Displays comprehensive analytics for a QR code with charts and data visualization
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import analyticsService, { ComprehensiveAnalytics } from '../services/analyticsService';
import qrcodeService from '../services/qrcodeService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const QRCodeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<ComprehensiveAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchAnalytics();
    }
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    let blobUrl: string | null = null;

    const loadQRImage = async () => {
      if (!id) return;

      try {
        const url = await qrcodeService.getImageBlobUrl(parseInt(id, 10), 'png');
        if (isMounted) {
          blobUrl = url;
          setQrImageUrl(url);
        }
      } catch (err) {
        console.error('Failed to load QR code image:', err);
        if (isMounted) {
          setError('Impossible de charger l\'image du QR code');
        }
      }
    };

    loadQRImage();

    return () => {
      isMounted = false;
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [id]);

  const fetchAnalytics = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await analyticsService.getComprehensiveAnalytics(parseInt(id, 10));
      setAnalytics(data);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!id) return;

    try {
      await analyticsService.exportScans(parseInt(id, 10), format);
    } catch (err: any) {
      console.error('Failed to export:', err);
      setError(err.response?.data?.error || 'Failed to export data');
    }
  };

  const handleCopyURL = () => {
    if (!analytics) return;

    const url = analyticsService.getRedirectURL(analytics.qrcode.shortcode);
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleDownloadQRCode = async () => {
    if (!id) return;

    try {
      await qrcodeService.downloadImage(parseInt(id, 10), 'png');
    } catch (err: any) {
      console.error('Failed to download QR code:', err);
      setError(err.response?.data?.error || 'Failed to download QR code');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/qrcodes')}>
          Retour aux codes QR
        </Button>
      </Container>
    );
  }

  if (!analytics) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Aucune donnée analytique disponible</Alert>
      </Container>
    );
  }

  const {
    statistics,
    device_types,
    browsers,
    operating_systems,
    countries,
    cities,
    scans_over_time,
    scans_by_hour,
    scans_by_day,
  } = analytics.analytics;

  // Chart configurations
  const timelineChartData = {
    labels: scans_over_time.map((item) => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Scans',
        data: scans_over_time.map((item) => item.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.3,
      },
    ],
  };

  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: 'Scans par heure',
        data: Array.from({ length: 24 }, (_, i) => {
          const hourData = scans_by_hour.find((item) => parseInt(item.hour as any) === i);
          return hourData ? hourData.count : 0;
        }),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
    ],
  };

  const weeklyChartData = {
    labels: scans_by_day.map((item) => item.day),
    datasets: [
      {
        label: 'Scans par jour',
        data: scans_by_day.map((item) => item.count),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
      },
    ],
  };

  const deviceChartData = {
    labels: device_types.map((item) => item.device_type),
    datasets: [
      {
        data: device_types.map((item) => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
        ],
      },
    ],
  };

  const browserChartData = {
    labels: browsers.slice(0, 5).map((item) => item.browser),
    datasets: [
      {
        data: browsers.slice(0, 5).map((item) => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
      },
    ],
  };

  const osChartData = {
    labels: operating_systems.slice(0, 5).map((item) => item.os),
    datasets: [
      {
        data: operating_systems.slice(0, 5).map((item) => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
      },
    ],
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/qrcodes')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {analytics.qrcode.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip label={analytics.qrcode.shortcode} size="small" color="primary" />
              <Tooltip title={copySuccess ? 'Copié !' : 'Copier l\'URL'}>
                <IconButton size="small" onClick={handleCopyURL}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAnalytics}
          >
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadQRCode}
          >
            Télécharger le QR
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>

      {/* QR Code Preview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Aperçu du code QR
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                }}
              >
                {qrImageUrl ? (
                  <img
                    src={qrImageUrl}
                    alt={analytics.qrcode.name}
                    style={{
                      width: '100%',
                      maxWidth: 300,
                      height: 'auto',
                    }}
                  />
                ) : (
                  <CircularProgress />
                )}
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={() => qrcodeService.downloadImage(parseInt(id!, 10), 'png')}
                >
                  Télécharger PNG
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={() => qrcodeService.downloadImage(parseInt(id!, 10), 'svg')}
                >
                  Télécharger SVG
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations du code QR
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Nom
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {analytics.qrcode.name}
                </Typography>

                {analytics.qrcode.description && (
                  <>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {analytics.qrcode.description}
                    </Typography>
                  </>
                )}

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  URL de destination
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    wordBreak: 'break-all',
                    color: 'primary.main',
                  }}
                >
                  {analytics.qrcode.destination_url}
                </Typography>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  URL courte
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {analyticsService.getRedirectURL(analytics.qrcode.shortcode)}
                  </Typography>
                  <Tooltip title={copySuccess ? 'Copié !' : 'Copier l\'URL'}>
                    <IconButton size="small" onClick={handleCopyURL}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Taille : ${analytics.qrcode.size}px`} size="small" />
                  <Chip label={`Correction d'erreur : ${analytics.qrcode.error_correction}`} size="small" />
                  <Chip label={`Style : ${analytics.qrcode.module_style}`} size="small" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Scans totaux
              </Typography>
              <Typography variant="h4">{statistics.total_scans}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Visiteurs uniques
              </Typography>
              <Typography variant="h4">{statistics.unique_visitors}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pays
              </Typography>
              <Typography variant="h4">{statistics.countries_count}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Villes
              </Typography>
              <Typography variant="h4">{statistics.cities_count}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Chronologie" />
          <Tab label="Appareils et navigateurs" />
          <Tab label="Localisations" />
        </Tabs>

        {/* Timeline Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Scans au fil du temps (30 derniers jours)
                </Typography>
                <Line data={timelineChartData} options={{ maintainAspectRatio: true }} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Scans par heure de la journée
                </Typography>
                <Bar data={hourlyChartData} options={{ maintainAspectRatio: true }} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Scans par jour de la semaine
                </Typography>
                <Bar data={weeklyChartData} options={{ maintainAspectRatio: true }} />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Devices & Browsers Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Types d'appareils
                </Typography>
                <Pie data={deviceChartData} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Navigateurs principaux
                </Typography>
                <Doughnut data={browserChartData} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Systèmes d'exploitation principaux
                </Typography>
                <Doughnut data={osChartData} />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Locations Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Pays principaux
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pays</TableCell>
                        <TableCell align="right">Scans</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {countries.slice(0, 10).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.country}</TableCell>
                          <TableCell align="right">{item.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Villes principales
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ville</TableCell>
                        <TableCell>Pays</TableCell>
                        <TableCell align="right">Scans</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cities.slice(0, 10).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.city}</TableCell>
                          <TableCell>{item.country}</TableCell>
                          <TableCell align="right">{item.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default QRCodeDetails;
